<template>
  <button
    class="s-btn"
    @click="handleMetamaskLogin"
    :disabled="isRequestingAccounts"
    type="button"
  >
    <span class="s-icon"><MetamaskLogo /></span>
    <span class="s-label">Connect Wallet</span>
  </button>
</template>

<script>
import { afterAuthPath } from '@/http/requests/billing';
import Web3 from 'web3';
import MetamaskLogo from '@/decast/icons/MetamaskLogo.vue';

export default {
  name: 'MetamaskLogin',
  components: {
    MetamaskLogo,
  },
  data() {
    return {
      publicAddress: '',
      signature: '',
      nonce: '',
      coinbase: '',
      isRequestingAccounts: false,
    };
  },
  methods: {
    async handleMetamaskLogin() {
      if (this.isRequestingAccounts) return;

      this.isRequestingAccounts = true;
      this.publicAddress = '';
      this.signature = '';
      this.nonce = '';
      this.coinbase = '';

      document.getElementById('loading-bg-transparent-title').innerText =
        'Connecting to MetaMask';
      document.getElementById('loading-bg-transparent').style.display = 'flex';

      try {
        const web3 = new Web3(Web3.givenProvider);
        if (!web3.givenProvider) {
          this.$vs.notify({
            title: this.$t('Login.notify.title'),
            text: this.$t('Metamasklogin.install'),
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'warning',
          });
          this.isRequestingAccounts = false;
          return;
        }

        const accounts = await this.withTimeout(
          window.ethereum.request({ method: 'eth_requestAccounts' }),
          15000
        );

        const account = accounts[0];
        const coinbase = await web3.eth.getCoinbase();

        if (!coinbase) {
          this.$vs.notify({
            title: this.$t('Login.notify.title'),
            text: this.$t('Metamasklogin.activate'),
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'warning',
          });
          this.isRequestingAccounts = false;
          return;
        }

        this.publicAddress = account.toLowerCase();
        this.$store
          .dispatch('auth/v2getNonce', { address: this.publicAddress })
          .then((res) => {
            this.handleSignMessage(
              this.publicAddress,
              res.data.nonce,
              res.data.token
            );
          })
          .catch(() => {
            this.$vs.notify({
              title: this.$t('Login.notify.title'),
              text: this.$t('Metamasklogin.tryagain'),
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger',
            });
          });
      } catch (error) {
        if (error.code === -32002) {
          this.$vs.notify({
            title: this.$t('Login.notify.title'),
            text: 'MetaMask is already processing a request. Please check the extension.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'warning',
          });
        } else if (error.message === 'MetaMask timeout') {
          this.$vs.notify({
            title: this.$t('Login.notify.title'),
            text: 'No response from MetaMask. You may have closed the popup.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'warning',
          });
        } else {
          console.error('MetaMask Login Error:', error);
        }
      } finally {
        this.isRequestingAccounts = false;
        document.getElementById('loading-bg-transparent').style.display =
          'none';
      }
    },

    async handleSignMessage(publicAddress, nonce, token) {
      document.getElementById('loading-bg-transparent-title').innerText =
        'Signing with MetaMask';
      document.getElementById('loading-bg-transparent').style.display = 'flex';
      try {
        const web3 = new Web3(Web3.givenProvider);
        const signature = await web3.eth.personal.sign(
          nonce,
          publicAddress,
          ''
        );
        this.signature = signature;
        this.nonce = nonce;
        this.$store
          .dispatch('auth/v2sendSignature', {
            token: token,
            signature: this.signature,
          })
          .then((response) => {
            const isTwoFactorAuthEnabled =
              response.data?.twoFactorAuthentication;
            if (isTwoFactorAuthEnabled) {
              const twoFactorToken = response.data?.token;
              this.$router.push({
                name: 'TwoFactor',
                query: { tempToken: twoFactorToken },
              });
            } else {
              this.$store.commit('auth/SET_LOGGEDIN', true);
              this.$acl.change('user');
              this.$vs.notify({
                title: this.$t('Login.notify.title'),
                text: 'Login Successfull',
                iconPack: 'feather',
                icon: 'icon-alert-circle',
                color: 'success',
              });
              this.$router.push(afterAuthPath('/dashboard/rooms'));
            }
          })
          .catch((err) => {
            console.log(err);
            this.$vs.loading.close();
            this.$vs.notify({
              title: this.$t('Login.notify.title'),
              text: this.$t('Metamasklogin.tryagain'),
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger',
            });
          })
          .finally(() => {
            document.getElementById('loading-bg-transparent').style.display =
              'none';
          });
      } catch (err) {
        this.$vs.notify({
          title: this.$t('Login.notify.title'),
          text: this.$t('Metamasklogin.signmessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning',
        });
      } finally {
        document.getElementById('loading-bg-transparent').style.display =
          'none';
      }
    },

    withTimeout(promise, timeout = 15000) {
      return Promise.race([
        promise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('MetaMask timeout')), timeout)
        ),
      ]);
    },
  },
};
</script>

<style scoped>
.s-btn {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #cdcfe0;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Rethink Sans', sans-serif;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  padding: 0;
}

.s-btn:hover {
  border-color: rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
}

.s-btn:active {
  transform: scale(0.98);
}

.s-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.s-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.s-icon svg {
  width: 18px;
  height: 18px;
}

.s-label {
  line-height: 1;
}
</style>
